/** usePeopleSearch: member hits for the palette. No fetch under the minimum length; debounced; the last hits stay put until the new ones land, so the list never jumps. A failed search is one row that tries again, never "Nothing matches". */
import { useEffect, useState } from "react";
import { MIN_QUERY_LENGTH, SEARCH_DEBOUNCE_MS, search } from "../../api/search";
import type { Entry } from "./entries";

const LIMIT = 6;

const FAILED: Entry = { key: "people:failed", group: "search-failed", label: "Search did not load", hint: "Try again", target: { kind: "retry-search" } };

interface State {
  term: string;
  rows: Entry[];
}

/** "Last, First" and "First Last" are the same words: the search takes them in any order. "ID 40312" is the sheet's own wording of a CE21 ID, so "ID" and "#" before a number are dropped. */
const words = (query: string): string => query.replace(/^\s*(?:id|#)\s*(?=\d)/i, "").replace(/,/g, " ").trim().replace(/\s+/g, " ");

export function usePeopleSearch(query: string): { rows: Entry[]; settled: boolean; retry: () => void } {
  const term = words(query);
  const active = term.length >= MIN_QUERY_LENGTH;
  const [state, setState] = useState<State>({ term: "", rows: [] });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      search(term, { limit: LIMIT, signal: controller.signal }).then(
        (response) => {
          const group = response.groups.find((g) => g.type === "member");
          const rows: Entry[] = (group?.results ?? []).map((r) => ({
            key: `person:${r.id}`,
            group: "people",
            label: r.label,
            hint: r.sublabel,
            match: r.match ? { start: r.match.start, length: r.match.length } : undefined,
            target: { kind: "person", id: r.id },
          }));
          setState({ term, rows });
        },
        () => {
          if (!controller.signal.aborted) setState({ term, rows: [FAILED] });
        },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [term, active, attempt]);

  const retry = () => {
    // Unsettled until the new answer lands, so Enter waits for it.
    setState({ term: "", rows: [] });
    setAttempt((n) => n + 1);
  };

  return { rows: active ? state.rows : [], settled: !active || state.term === term, retry };
}
